export default {
  index: {
    title: 'Ratgeber zu Gezeiten und Strömungen',
    description:
      'Erfahren Sie, wie Gezeiten und Gezeitenströmungen funktionieren: Tidenkoeffizienten, Flut- und Ebbströme, Gezeitenbezugshorizonte und wie Gezeitenvorhersagen berechnet werden.',
    subtitle:
      'Kurze, allgemein verständliche Erklärungen zur Wissenschaft hinter Gezeiten- und Strömungsvorhersagen.',
  },
  back: 'Zurück zu allen Ratgebern',
  guides: {
    'tidal-coefficients': {
      title: 'Tidenkoeffizienten erklärt',
      description:
        'Was Tidenkoeffizienten bedeuten: die Skala von 20 bis 120, wie Spring- und Nipptiden mit dem Mond zusammenhängen und warum ein höherer Koeffizient einen größeren Tidenhub bringt.',
      blurb:
        'Die Skala von 20 bis 120, die zusammenfasst, wie groß eine Tide ausfällt, und wie der Mond sie antreibt.',
      body: [
        {
          p: 'Ein Tidenkoeffizient ist eine einzelne Zahl, die zusammenfasst, wie groß eine Tide an einem bestimmten Tag ausfällt. Er ist in Frankreich und anderen Ländern verbreitet, die der französischen hydrographischen Tradition folgen. Der Koeffizient ist dimensionslos und liegt normalerweise zwischen etwa 20 und 120: Je größer die Zahl, desto größer der Unterschied zwischen Hoch- und Niedrigwasser und desto stärker die zugehörigen Gezeitenströmungen.',
        },
        { h2: 'Die Skala von 20 bis 120' },
        {
          p: 'Der Koeffizient vergleicht den Tidenhub des Tages mit einem langfristigen Bezugswert. Mehrere Punkte auf der Skala haben eine übliche Bedeutung:',
        },
        {
          list: [
            {
              term: '20',
              text: 'die kleinsten Tiden, nahe dem minimalen astronomischen Tidenhub.',
            },
            { term: '45', text: 'eine durchschnittliche Nipptide, wenn der Tidenhub klein ist.' },
            {
              term: '70',
              text: 'eine mittlere Tide, ungefähr die Mitte zwischen Nipp- und Springtiden.',
            },
            { term: '95', text: 'eine durchschnittliche Springtide, wenn der Tidenhub groß ist.' },
            { term: '120', text: 'die größten Tiden, nahe dem maximalen astronomischen Tidenhub.' },
          ],
        },
        { h2: 'Warum er sich ändert: der Mond' },
        {
          p: 'Die Koeffizienten steigen und fallen im etwa zweiwöchigen Spring-Nipp-Zyklus. Springtiden mit hohen Koeffizienten treten ein oder zwei Tage nach jedem Neu- und Vollmond auf, wenn Sonne und Mond in einer Linie ziehen und ihre Gezeitenkräfte sich addieren. Nipptiden mit niedrigen Koeffizienten treten nahe dem ersten und letzten Viertelmond auf, wenn Sonne und Mond im rechten Winkel stehen und sich teilweise aufheben.',
        },
        {
          p: 'Auch die Entfernung des Mondes spielt eine Rolle. Wenn eine Springtide mit dem Perigäum (der größten Annäherung des Mondes an die Erde) zusammenfällt, erreicht der Koeffizient seine höchsten Werte. Die allergrößten Tiden treten meist nahe der Frühjahrs- und Herbst-Tagundnachtgleiche gehäuft auf.',
        },
        { h2: 'Was ein Koeffizient nicht verrät' },
        {
          p: 'Der Koeffizient beschreibt nur den astronomischen Tidenhub. Er berücksichtigt nicht den Einfluss des Wetters: Wind und Luftdruck können den tatsächlichen Wasserstand unabhängig vom Koeffizienten heben oder senken. Zudem ist er eine Eigenschaft der Tide als Ganzes an diesem Tag, keine Höhe in Metern oder Fuß an einem einzelnen Ort.',
        },
      ],
    },
    'tidal-currents': {
      title: 'Flut, Ebbe und Stillwasser: Wie Gezeitenströmungen funktionieren',
      description:
        'Wie Gezeitenströmungen funktionieren: Flut- und Ebbrichtung, Stillwasser beim Kentern, Strömungsgeschwindigkeit in Knoten und Stromrichtung, und warum Stillwasser nicht immer mit Hoch- und Niedrigwasser zusammenfällt.',
      blurb:
        'Flut, Ebbe und Stillwasser, und warum die stärkste Strömung nicht immer bei Hochwasser auftritt.',
      body: [
        {
          p: 'Gezeitenströmungen sind die horizontale Wasserbewegung, die das vertikale Steigen und Fallen der Tide begleitet. Während die Tidenhöhe angibt, wie tief das Wasser ist, verrät die Strömung, in welche Richtung und wie schnell es fließt. Am wichtigsten sind Strömungen in Fahrrinnen, Seegatten und Ästuaren, wo sie mehrere Knoten erreichen können.',
        },
        { h2: 'Flut, Ebbe und Stillwasser' },
        {
          list: [
            {
              term: 'Flutstrom',
              text: 'Wasser, das zur Küste hin oder ein Ästuar hinauf fließt, im Allgemeinen während die Tide steigt.',
            },
            {
              term: 'Ebbstrom',
              text: 'Wasser, das seewärts oder ein Ästuar hinab fließt, im Allgemeinen während die Tide fällt.',
            },
            {
              term: 'Stillwasser',
              text: 'die kurze Pause, in der die Strömung nahezu null ist, während sie zwischen Flut und Ebbe kentert.',
            },
          ],
        },
        {
          p: 'Die Strömungsgeschwindigkeit wird in Knoten (Seemeilen pro Stunde) gemessen. Ihre Richtung, Stromrichtung genannt, wird als Kompasspeilung angegeben: die Richtung, in die das Wasser fließt.',
        },
        { h2: 'Stillwasser ist nicht immer bei Hochwasser' },
        {
          p: 'Eine verbreitete Annahme ist, dass die Strömung bei Hoch- und Niedrigwasser zum Stillstand kommt. Das stimmt nur an manchen Orten. Der Zeitpunkt hängt davon ab, wie sich die Tidenwelle örtlich verhält. Wo sich die Tide wie eine fortschreitende Welle verhält, etwa in einer langen offenen Rinne, treten die stärkste Flut und Ebbe nahe Hoch- und Niedrigwasser auf, und das Stillwasser fällt etwa auf die Mitte. Wo sie sich wie eine stehende Welle verhält, etwa am Ende einer geschlossenen Bucht, ist es umgekehrt: Die schnellste Strömung tritt nahe der halben Tide auf, und das Stillwasser fällt nahe Hoch- und Niedrigwasser. Örtliche Stromtafeln sind der verlässliche Leitfaden.',
        },
        { h2: 'Umkehrende und drehende Strömungen' },
        {
          p: 'In engen Rinnen ist die Strömung meist umkehrend: Sie läuft bei Flut in die eine Richtung, kentert dann und läuft bei Ebbe zurück. In offenem Küstengewässer ist die Strömung stattdessen oft drehend und ändert ihre Richtung über einen Gezeitenzyklus kontinuierlich durch alle Kompassrichtungen, statt entlang einer einzigen Linie umzukehren.',
        },
      ],
    },
    'tidal-datums': {
      title: 'Gezeitenbezugshorizonte und Tidenhöhen',
      description:
        'Worauf sich eine Tidenhöhe bezieht: Seekartennull, niedrigstmögliches Gezeitenniedrigwasser (LAT), mittleres niedrigeres Niedrigwasser (MLLW) und warum sich die Bezugshorizonte zwischen Ländern unterscheiden.',
      blurb: 'Seekartennull, LAT, MLLW und was die Zahl in einer Tidenhöhe tatsächlich bedeutet.',
      body: [
        {
          p: 'Jede Tidenhöhe ist eine Messung über einem Bezugsniveau, das als Bezugshorizont bezeichnet wird. Wenn eine Gezeitentafel angibt, dass das Wasser 1.8 Meter erreicht, sind damit 1.8 Meter über dem örtlichen Gezeitenbezugshorizont gemeint: nicht über dem Meeresboden und nicht über dem Meeresspiegel im alltäglichen Sinn. Erst die Kenntnis des Bezugshorizonts macht eine Höhe aussagekräftig.',
        },
        { h2: 'Was ein Gezeitenbezugshorizont ist' },
        {
          p: 'Ein Gezeitenbezugshorizont ist eine Bezugshöhe, die durch einen bestimmten Gezeitenstand definiert und über einen langen Zeitraum gemittelt wird: üblicherweise etwa 19 Jahre, was den 18.6-jährigen Zyklus der Mondbahn ausgleicht. Da er ein Mittelwert realer Beobachtungen ist, ist ein Bezugshorizont spezifisch für einen Ort und für die Behörde, die ihn berechnet hat.',
        },
        { h2: 'Seekartennull und gängige Bezugshorizonte' },
        {
          p: 'Seekarten verwenden einen Niedrigwasser-Bezugshorizont, damit die tatsächliche Tiefe fast immer mindestens der eingetragenen Tiefe entspricht. Dieses Seekartennull ist der Nullpunkt, von dem aus sowohl die eingetragenen Tiefen als auch die Tidenhöhen gerechnet werden. Verschiedene Länder verwenden unterschiedliche Bezugshorizonte:',
        },
        {
          list: [
            {
              term: 'LAT (niedrigstmögliches Gezeitenniedrigwasser)',
              text: 'das niedrigste unter durchschnittlichen Wetterbedingungen vorhersagbare Niveau; verwendet vom Vereinigten Königreich und vielen anderen Nationen.',
            },
            {
              term: 'MLLW (mittleres niedrigeres Niedrigwasser)',
              text: 'der Mittelwert des jeweils niedrigeren der beiden täglichen Niedrigwasser; das Standard-Seekartennull in den Vereinigten Staaten.',
            },
            {
              term: 'MSL (mittlerer Meeresspiegel)',
              text: 'der langfristige mittlere Wasserstand, hauptsächlich als Bezug für Landhöhen und weniger für Seekarten verwendet.',
            },
          ],
        },
        {
          p: 'Hochwasser-Bezugshorizonte wie das mittlere Hochwasser (MHW) und das mittlere höhere Hochwasser (MHHW) dienen dem entgegengesetzten Zweck, etwa der Festlegung einer Uferlinie oder einer Durchfahrtshöhe unter einer Brücke.',
        },
        { h2: 'Warum Höhen nicht direkt vergleichbar sind' },
        {
          p: 'Da sich die Bezugshorizonte unterscheiden, lässt sich eine Höhe an einer Station nicht direkt mit einer Höhe an einer anderen vergleichen, sofern nicht beide denselben Bezug verwenden. Ein von LAT gemessenes Niedrigwasser von 0.5 Meter entspricht nicht demselben Wasserstand wie ein von MLLW gemessenes Niedrigwasser von 0.5 Meter. Auch deshalb kann dieselbe physikalische Tide in den Tafeln verschiedener Länder durch unterschiedliche Zahlen beschrieben werden.',
        },
      ],
    },
    'how-tide-predictions-work': {
      title: 'Wie Gezeitenvorhersagen berechnet werden',
      description:
        'Wie Gezeiten vorhergesagt werden: die Anziehungskräfte von Mond und Sonne, harmonische Partialtiden wie M2 und S2 und warum beobachtete Wasserstände von der astronomischen Vorhersage abweichen.',
      blurb:
        'Harmonische Analyse, die Partialtiden hinter der Gezeit und warum das Wetter die Wirklichkeit abweichen lässt.',
      body: [
        {
          p: 'Gezeiten lassen sich Jahre im Voraus vorhersagen, weil sie von den Bewegungen von Mond und Sonne angetrieben werden, die mit großer Präzision bekannt sind. Die Tide an einem Ort ist die Reaktion des Ozeans auf diese Anziehungskräfte, geformt durch die örtliche Küstenlinie und Wassertiefe. Das Verfahren, das Astronomie in eine Gezeitentafel umsetzt, heißt harmonische Analyse.',
        },
        { h2: 'Die Tide als Summe von Partialtiden' },
        {
          p: 'Die harmonische Analyse betrachtet die Tide als Summe vieler einfacher Wellen, die jeweils Partialtide genannt werden. Jede Partialtide hat eine durch die Astronomie festgelegte Frequenz: durch die Bewegung von Erde, Mond und Sonne zueinander: , während ihre Amplitude und ihr zeitlicher Verlauf durch Anpassung der Partialtiden an einen Zeitraum realer Wasserstandsbeobachtungen an diesem Ort bestimmt werden. Zu den wichtigsten Partialtiden gehören:',
        },
        {
          list: [
            {
              term: 'M2',
              text: 'die halbtägige Hauptmondtide, Periode 12.42 Stunden; meist die größte.',
            },
            { term: 'S2', text: 'die halbtägige Hauptsonnentide, Periode 12.00 Stunden.' },
            {
              term: 'N2',
              text: 'eine Mondpartialtide, die aus der elliptischen Mondbahn entsteht.',
            },
            {
              term: 'K1 und O1',
              text: 'die wichtigsten ganztägigen Partialtiden, die an vielen Orten das eine tägliche Hochwasser größer machen als das andere.',
            },
          ],
        },
        {
          p: 'Der bekannte Spring-Nipp-Zyklus ergibt sich daraus ganz natürlich: M2 und S2 haben leicht unterschiedliche Perioden, sodass sie in und außer Takt geraten: alle 14.8 Tage verstärken sie sich zu Springtiden und heben sich teilweise zu Nipptiden auf.',
        },
        { h2: 'Vorhersage in die Zukunft' },
        {
          p: 'Sobald Amplitude und Phase jeder Partialtide für einen Ort bekannt sind, ist die Vorhersage der Tide nur noch eine Frage der Auswertung der Summe zu einem beliebigen zukünftigen Zeitpunkt. Die astronomischen Frequenzen ändern sich nie, sodass die Berechnung deterministisch ist und Jahrzehnte im Voraus laufen kann.',
        },
        { h2: 'Warum die tatsächliche Tide abweicht' },
        {
          p: 'Eine Vorhersage ist nur die astronomische Tide. Sie berücksichtigt das Wetter nicht. Starker Wind, niedriger oder hoher Luftdruck, starker Regen und Flusszufluss können den tatsächlichen Wasserstand heben oder senken: eine Sturmflut kann einen Meter oder mehr über die Vorhersage hinaus hinzufügen, während hoher Luftdruck und ablandiger Wind das Wasser deutlich darunter halten können. Vorhersagen sind daher ein ausgezeichneter Anhaltspunkt für Zeitpunkt und Größe der Tide, doch der beobachtete Wasserstand am Tag selbst kann abweichen.',
        },
      ],
    },
  },
}
